import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle2, Phone, Upload } from 'lucide-react';
import { mockData } from '../mockData';
import { WorldMap } from '../components/WorldMap';
import { Helmet } from 'react-helmet';

export const HomePage = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Pharmaceutical Regulatory Services India | Pharma Export Consultancy Africa Asia</title>
        <meta
          name="description"
          content="Leading pharma regulatory consultancy India. ACTD/CTD dossier preparation, regulatory gap analysis, artwork compliance for Africa, Asia, GCC exports. Faster approvals for SMEs."
        />
        <meta name="keywords" content="pharmaceutical regulatory services, pharma export consultancy, ACTD dossier preparation, CTD dossier preparation, regulatory gap analysis, Africa pharma market entry, ASEAN pharma market entry, nutraceutical regulatory services, pharma regulatory consultancy India, regulatory affairs outsourcing" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.theapostrophe.com/" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Pharmaceutical Regulatory Services India | Expert Export Consultancy" />
        <meta property="og:description" content="ACTD/CTD dossier preparation, regulatory gap analysis, and artwork compliance for pharma exports to Africa, Asia, GCC, LATAM." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.theapostrophe.com/" />
        <meta property="og:image" content="https://www.theapostrophe.com/og-image.jpg" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pharmaceutical Regulatory Services India" />
        <meta name="twitter:description" content="Expert pharma regulatory consultancy for Africa, Asia, GCC exports. Faster product approvals." />
        
        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "TheApostrophe",
            "description": "Pharmaceutical and nutraceutical regulatory consultancy services for international exports",
            "url": "https://www.theapostrophe.in",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "B-319, Aarohi Galleria, South Bopal",
              "addressLocality": "Ahmedabad",
              "postalCode": "380058",
              "addressCountry": "IN"
            },
            "telephone": "+91-9714485551",
            "email": "hello@theapostrophe.in",
            "areaServed": ["Africa", "Asia", "GCC", "Latin America"],
            "serviceType": ["Pharmaceutical Regulatory Services", "Dossier Preparation", "Regulatory Gap Analysis", "Artwork Compliance"]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 px-6 py-2 text-sm" style={{ backgroundColor: '#A060B0', color: 'white' }}>
                Regulatory Affairs Specialists
              </Badge>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight" style={{ color: '#303040' }}>
                {mockData.hero.title}
              </h1>
              <p className="text-xl md:text-2xl mb-12 leading-relaxed" style={{ color: '#708080' }}>
                {mockData.hero.subtitle}
              </p>
            </div>
            <div className="flex flex-col gap-4">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl w-full"
                  style={{ backgroundColor: '#606090', color: 'white' }}
                >
                  {mockData.hero.primaryCTA}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/countries">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 w-full"
                  style={{ borderColor: '#708080', color: '#303040' }}
                >
                  {mockData.hero.secondaryCTA}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Help Section - Magazine Style */}
      <section className="py-24 px-6 overflow-hidden" style={{ backgroundColor: 'rgba(112, 128, 128, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <h2 className="text-4xl md:text-6xl font-bold mb-4" style={{ color: '#303040' }}>
              Who We Help
            </h2>
            <div className="w-24 h-1" style={{ backgroundColor: '#A060B0' }} />
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Featured First Item - Large */}
            <div className="space-y-8">
              <div className="relative overflow-hidden">
                <div 
                  className="absolute -left-4 -top-4 w-24 h-24 md:w-32 md:h-32 rounded-3xl opacity-20"
                  style={{ backgroundColor: mockData.whoWeHelp[0].color }}
                />
                <div className="relative bg-white p-10 rounded-2xl shadow-xl">
                  <div className="flex items-start gap-6">
                    <div
                      className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: mockData.whoWeHelp[0].color }}
                    >
                      {React.createElement(mockData.whoWeHelp[0].icon, { className: "h-10 w-10 text-white" })}
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold mb-4" style={{ color: '#303040' }}>
                        {mockData.whoWeHelp[0].title}
                      </h3>
                      <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                        {mockData.whoWeHelp[0].description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Second Item - Medium */}
              <div 
                className="p-8 rounded-2xl text-white"
                style={{ background: `linear-gradient(135deg, ${mockData.whoWeHelp[1].color} 0%, ${mockData.whoWeHelp[1].color}dd 100%)` }}
              >
                <div className="flex items-center gap-4 mb-4">
                  {React.createElement(mockData.whoWeHelp[1].icon, { className: "h-12 w-12 text-white" })}
                  <h3 className="text-2xl font-bold">
                    {mockData.whoWeHelp[1].title}
                  </h3>
                </div>
                <p className="text-lg text-white/90 leading-relaxed">
                  {mockData.whoWeHelp[1].description}
                </p>
              </div>
            </div>

            {/* Third Item - Stacked Style */}
            <div className="bg-white p-10 rounded-2xl shadow-xl h-full flex flex-col justify-center">
              <div 
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                style={{ backgroundColor: mockData.whoWeHelp[2].color + '20' }}
              >
                {React.createElement(mockData.whoWeHelp[2].icon, { className: "h-8 w-8", style: { color: mockData.whoWeHelp[2].color } })}
              </div>
              <h3 className="text-3xl font-bold mb-6" style={{ color: '#303040' }}>
                {mockData.whoWeHelp[2].title}
              </h3>
              <p className="text-xl leading-relaxed" style={{ color: '#708080' }}>
                {mockData.whoWeHelp[2].description}
              </p>
              <div className="mt-8 pt-8 border-t" style={{ borderColor: '#70808030' }}>
                <Link to="/about">
                  <Button
                    className="rounded-full"
                    style={{ backgroundColor: mockData.whoWeHelp[2].color, color: 'white' }}
                  >
                    Learn More
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Staggered Magazine Layout */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ color: '#303040' }}>
                What We Do
              </h2>
              <p className="text-xl leading-relaxed" style={{ color: '#708080' }}>
                End-to-end regulatory support from strategy to approval — tailored for LATAM, GCC, Africa & Asia.
              </p>
            </div>
          </div>
          
          {/* Dynamic Grid Layout */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.services.map((service, index) => {
              // Alternate styling patterns for magazine feel
              const isLarge = index === 0 || index === 3;
              const isAccent = index === 2 || index === 5;
              const ServiceIcon = service.icon;
              
              return (
                <div
                  key={index}
                  className={`group transition-all duration-300 hover:-translate-y-2 ${
                    isLarge ? 'lg:col-span-2' : ''
                  }`}
                >
                  {isAccent ? (
                    // Accent style with colored background
                    <div
                      className="h-full p-8 rounded-2xl text-white shadow-lg hover:shadow-2xl transition-shadow duration-300"
                      style={{ backgroundColor: service.color }}
                    >
                      <ServiceIcon className="h-10 w-10 text-white mb-4" />
                      <h3 className="text-2xl font-bold mb-4">
                        {service.title}
                      </h3>
                      <p className="text-white/90 leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  ) : (
                    // Standard white background with icon
                    <div className="h-full bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300">
                      <div className="flex items-start gap-4 mb-4">
                        <div
                          className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: service.color + '20' }}
                        >
                          <ServiceIcon className="h-7 w-7" style={{ color: service.color }} />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold mb-2" style={{ color: '#303040' }}>
                            {service.title}
                          </h3>
                        </div>
                      </div>
                      <p className="leading-relaxed" style={{ color: '#708080' }}>
                        {service.description}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Trust Us Section - Split Layout */}
      <section className="py-24 px-6 overflow-hidden" style={{ backgroundColor: 'rgba(96, 96, 144, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Left Side - Title & Intro */}
            <div className="lg:col-span-2">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{ color: '#303040' }}>
                Why Exporters Trust TheApostrophe
              </h2>
              <div className="w-20 h-1 mb-6" style={{ backgroundColor: '#606090' }} />
              <p className="text-lg leading-relaxed mb-8" style={{ color: '#708080' }}>
                Specialized expertise, predictable workflows, and founder-led execution that removes regulatory bottlenecks.
              </p>
              <Link to="/about">
                <Button
                  className="rounded-full"
                  style={{ backgroundColor: '#606090', color: 'white' }}
                >
                  Our Approach
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            {/* Right Side - Benefits in flowing layout */}
            <div className="lg:col-span-3 space-y-6">
              {mockData.whyTrustUs.map((item, index) => {
                const isEven = index % 2 === 0;
                const ItemIcon = item.icon;
                return (
                  <div
                    key={index}
                    className={`flex gap-6 items-start ${isEven ? 'lg:ml-0' : 'lg:ml-12'} transition-all duration-300 hover:translate-x-2`}
                  >
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md"
                      style={{ backgroundColor: item.color }}
                    >
                      <ItemIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex-1 bg-white p-6 rounded-xl shadow-sm">
                      <h3 className="text-lg font-bold mb-2" style={{ color: '#303040' }}>
                        {item.title}
                      </h3>
                      <p className="leading-relaxed" style={{ color: '#708080' }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Regulatory Launch Program - Timeline Style */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ color: '#303040' }}>
              Our Regulatory Launch Program
            </h2>
            <p className="text-2xl max-w-3xl" style={{ color: '#708080' }}>
              A 4-phase system designed to take your product from concept to country approval.
            </p>
          </div>
          
          {/* Vertical Timeline for Desktop, Stacked for Mobile */}
          <div className="space-y-8">
            {mockData.launchProgram.map((phase, index) => {
              const isEven = index % 2 === 0;
              return (
                <div
                  key={index}
                  className={`grid lg:grid-cols-2 gap-8 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                >
                  {/* Phase Number & Line */}
                  <div className={`${!isEven ? 'lg:order-2 lg:text-right' : ''}`}>
                    <div className={`flex items-center gap-6 ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                      <div
                        className="w-24 h-24 rounded-3xl flex items-center justify-center flex-shrink-0 shadow-xl"
                        style={{ backgroundColor: phase.color }}
                      >
                        <span className="text-4xl font-bold text-white">{index + 1}</span>
                      </div>
                      <div className="flex-1 h-1 hidden lg:block" style={{ backgroundColor: phase.color + '40' }} />
                    </div>
                  </div>

                  {/* Phase Content */}
                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <div className="bg-white p-8 rounded-2xl shadow-lg">
                      <h3 className="text-3xl font-bold mb-3" style={{ color: '#303040' }}>
                        {phase.title}
                      </h3>
                      <h4 className="text-xl font-semibold mb-4" style={{ color: phase.color }}>
                        {phase.subtitle}
                      </h4>
                      <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                        {phase.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-16">
            <Link to="/launch-program">
              <Button
                size="lg"
                className="px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#606090', color: 'white' }}
              >
                Learn About the 4-Phase Program
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Success Packs Section - Dynamic Grid */}
      <section className="py-24 px-6 overflow-hidden" style={{ backgroundColor: 'rgba(112, 128, 128, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
                Structured Packs for Faster Approvals
              </h2>
              <div className="w-20 h-1" style={{ backgroundColor: '#7090A0' }} />
            </div>
            <div className="flex items-end">
              <p className="text-xl leading-relaxed" style={{ color: '#708080' }}>
                Five pre-defined engagement models — from sourcing partners to lifecycle management. Pick what fits your stage.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {mockData.successPacks.map((pack, index) => {
              const isLarge = index === 0 || index === 3;
              return (
                <Link
                  key={index}
                  to={`/success-packs/${pack.slug}`}
                  className={`group cursor-pointer transition-all duration-300 hover:-translate-y-2 ${
                    isLarge ? '' : ''
                  }`}
                >
                  <div
                    className="h-full p-8 rounded-2xl shadow-lg hover:shadow-2xl relative overflow-hidden"
                    style={{
                      background: `linear-gradient(135deg, ${pack.color}15 0%, ${pack.color}05 100%)`
                    }}
                  >
                    <div
                      className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10 -mr-16 -mt-16"
                      style={{ backgroundColor: pack.color }}
                    />
                    <div
                      className="inline-flex items-center justify-center px-4 py-2 rounded-full text-white text-sm font-bold mb-6"
                      style={{ backgroundColor: pack.color }}
                    >
                      Pack {index + 1}
                    </div>
                    <h3 className="text-2xl font-bold mb-3" style={{ color: '#303040' }}>
                      {pack.name}
                    </h3>
                    <p className="text-lg mb-4 font-medium" style={{ color: pack.color }}>
                      {pack.subtitle}
                    </p>
                    <p className="leading-relaxed" style={{ color: '#708080' }}>
                      {pack.description}
                    </p>
                    <div className="mt-6">
                      <span
                        className="inline-flex items-center font-semibold group-hover:gap-2 transition-all"
                        style={{ color: pack.color }}
                      >
                        Learn More
                        <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/success-packs">
              <Button
                size="lg"
                className="px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#7090A0', color: 'white' }}
              >
                View All Success Packs
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Countries We Serve - Interactive Map & Regions */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ color: '#303040' }}>
              Countries We Serve
            </h2>
            <div className="w-24 h-1 mb-6" style={{ backgroundColor: '#A060B0' }} />
            <p className="text-2xl max-w-3xl" style={{ color: '#708080' }}>
              Expert regulatory support across four high-growth regions — LATAM, GCC, Africa & Asia
            </p>
          </div>
          
          {/* 4 Region Groupings - Magazine Style */}
          <div className="mt-20 space-y-16">
            {/* Africa */}
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              <div
                className="lg:col-span-1 rounded-3xl p-12 shadow-xl transition-all duration-300 hover:shadow-2xl"
                style={{ background: 'linear-gradient(135deg, #708080 0%, #809090 100%)' }}
              >
                <h3 className="text-4xl font-bold text-white mb-2">Africa</h3>
                <p className="text-xl text-white/90 mb-6">Growing pharmaceutical markets</p>
                <div className="text-5xl font-bold text-white/80">9</div>
                <p className="text-lg text-white/70">Countries</p>
              </div>
              <div className="lg:col-span-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries({
                    'Kenya': 'kenya',
                    'Nigeria': 'nigeria-pharma-nutra-registration',
                    'Tanzania': 'tanzania',
                    'Uganda': 'uganda-pharma-nutra-registration',
                    'Benin': 'benin-pharma-nutra-registration',
                    'Senegal': 'senegal-pharma-nutra-registration',
                    'Togo': 'togo-pharma-nutra-registration',
                    'Mozambique': 'mozambique-pharma-nutra-registration',
                    'Ghana': 'ghana-pharma-nutra-registration'
                  }).map(([name, slug]) => (
                    <Link
                      key={slug}
                      to={`/country/${slug}`}
                      className="group block p-4 rounded-xl bg-white shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold" style={{ color: '#303040' }}>{name}</span>
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" style={{ color: '#708080' }} />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* GCC */}
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              <div className="lg:col-span-2 order-2 lg:order-1">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries({
                    'Bahrain': 'bahrain-pharma-nutra-registration',
                    'Kuwait': 'kuwait',
                    'Oman': 'oman-pharma-nutra-registration',
                    'Qatar': 'qatar-pharma-nutra-registration',
                    'Saudi Arabia': 'saudi-arabia-pharma-nutra-registration',
                    'UAE': 'united-arab-emirates-pharma-nutra-registration'
                  }).map(([name, slug]) => (
                    <Link
                      key={slug}
                      to={`/country/${slug}`}
                      className="group block p-4 rounded-xl bg-white shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold" style={{ color: '#303040' }}>{name}</span>
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" style={{ color: '#A060B0' }} />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
              <div
                className="lg:col-span-1 rounded-3xl p-12 shadow-xl transition-all duration-300 hover:shadow-2xl order-1 lg:order-2"
                style={{ background: 'linear-gradient(135deg, #A060B0 0%, #B070C0 100%)' }}
              >
                <h3 className="text-4xl font-bold text-white mb-2">GCC</h3>
                <p className="text-xl text-white/90 mb-6">High-value Gulf markets</p>
                <div className="text-5xl font-bold text-white/80">6</div>
                <p className="text-lg text-white/70">Countries</p>
              </div>
            </div>

            {/* Asia */}
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              <div
                className="lg:col-span-1 rounded-3xl p-12 shadow-xl transition-all duration-300 hover:shadow-2xl"
                style={{ background: 'linear-gradient(135deg, #7090A0 0%, #80A0B0 100%)' }}
              >
                <h3 className="text-4xl font-bold text-white mb-2">Asia</h3>
                <p className="text-xl text-white/90 mb-6">Emerging wellness markets</p>
                <div className="text-5xl font-bold text-white/80">9</div>
                <p className="text-lg text-white/70">Countries</p>
              </div>
              <div className="lg:col-span-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries({
                    'Cambodia': 'cambodia',
                    'Myanmar': 'myanmar-pharma-nutra-registration',
                    'Sri Lanka': 'sri-lanka-pharma-nutra-registration',
                    'Nepal': 'nepal-pharma-nutra-registration',
                    'Mauritius': 'mauritius-pharma-nutra-registration',
                    'Malaysia': 'malaysia-pharma-nutra-registration',
                    'Bangladesh': 'bangladesh-pharma-nutra-registration',
                    'Bhutan': 'bhutan-pharma-nutra-registration',
                    'Maldives': 'maldives-pharma-nutra-registration'
                  }).map(([name, slug]) => (
                    <Link
                      key={slug}
                      to={`/country/${slug}`}
                      className="group block p-4 rounded-xl bg-white shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold" style={{ color: '#303040' }}>{name}</span>
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" style={{ color: '#7090A0' }} />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Latin America */}
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              <div className="lg:col-span-2 order-2 lg:order-1">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries({
                    'Mexico': 'mexico-pharma-nutra-registration',
                    'Colombia': 'colombia-pharma-nutra-registration',
                    'Peru': 'peru-pharma-nutra-registration',
                    'Chile': 'chile-pharma-nutra-registration',
                    'Dominican Republic': 'dominican-republic-pharma-nutra-registration',
                    'Panama': 'panama-pharma-nutra-registration',
                    'Paraguay': 'paraguay-pharma-nutra-registration',
                    'Costa Rica': 'costa-rica-pharma-nutra-registration',
                    'Uruguay': 'uruguay-pharma-nutra-registration'
                  }).map(([name, slug]) => (
                    <Link
                      key={slug}
                      to={`/country/${slug}`}
                      className="group block p-4 rounded-xl bg-white shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold" style={{ color: '#303040' }}>{name}</span>
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" style={{ color: '#606090' }} />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
              <div
                className="lg:col-span-1 rounded-3xl p-12 shadow-xl transition-all duration-300 hover:shadow-2xl order-1 lg:order-2"
                style={{ background: 'linear-gradient(135deg, #606090 0%, #7070A0 100%)' }}
              >
                <h3 className="text-4xl font-bold text-white mb-2">Latin America</h3>
                <p className="text-xl text-white/90 mb-6">Strategic LATAM expansion</p>
                <div className="text-5xl font-bold text-white/80">9</div>
                <p className="text-lg text-white/70">Countries</p>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-16">
            <Link to="/countries">
              <Button
                size="lg"
                className="px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A060B0', color: 'white' }}
              >
                Explore All Country Pages
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials - Magazine Layout - temporarily hidden
      <section className="py-24 px-6 overflow-hidden" style={{ backgroundColor: 'rgba(96, 96, 144, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ color: '#303040' }}>
              What Our Clients Say
            </h2>
            <div className="w-24 h-1" style={{ backgroundColor: '#606090' }} />
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.testimonials.map((testimonial, index) => {
              const isLarge = index === 0;
              return (
                <div
                  key={index}
                  className={`${isLarge ? 'lg:col-span-2' : ''} transition-all duration-300 hover:-translate-y-2`}
                >
                  <div
                    className="h-full p-8 rounded-2xl shadow-lg relative overflow-hidden"
                    style={{ backgroundColor: 'white' }}
                  >
                    <div
                      className="absolute top-0 left-0 w-2 h-full"
                      style={{ backgroundColor: testimonial.color }}
                    />
                    <div className="pl-6">
                      <div className="text-6xl mb-4 opacity-20" style={{ color: testimonial.color }}>
                        &ldquo;
                      </div>
                      <p className={`${isLarge ? 'text-xl' : 'text-lg'} leading-relaxed mb-6 italic`} style={{ color: '#708080' }}>
                        {testimonial.quote}
                      </p>
                      <div className="flex items-center gap-4 pt-4 border-t" style={{ borderColor: '#70808020' }}>
                        <div
                          className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl"
                          style={{ backgroundColor: testimonial.color + '20', color: testimonial.color }}
                        >
                          {testimonial.name.charAt(0)}
                        </div>
                        <div>
                          <h4 className="font-bold" style={{ color: '#303040' }}>
                            {testimonial.name}
                          </h4>
                          <p className="text-sm" style={{ color: '#708080' }}>
                            {testimonial.role}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      */}

      {/* Blog Preview - Magazine Grid */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ color: '#303040' }}>
              From Our Regulatory Desk
            </h2>
            <div className="w-24 h-1" style={{ backgroundColor: '#A060B0' }} />
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.blogPosts.map((post, index) => {
              const isFeatured = index === 0;
              return (
                <Link
                  key={index}
                  to={`/blog/${post.slug}`}
                  className={`${isFeatured ? 'lg:col-span-2 lg:row-span-2' : ''} group cursor-pointer`}
                >
                  <div className="h-full bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
                    {/* Image Area */}
                    <div
                      className={`${isFeatured ? 'h-80' : 'h-48'} relative`}
                      style={{ backgroundColor: post.color + '20' }}
                    >
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className="w-32 h-32 rounded-full opacity-30"
                          style={{ backgroundColor: post.color }}
                        />
                      </div>
                      <Badge
                        className="absolute top-4 left-4"
                        style={{ backgroundColor: post.color, color: 'white' }}
                      >
                        {post.category}
                      </Badge>
                    </div>
                    
                    {/* Content */}
                    <div className={`${isFeatured ? 'p-10' : 'p-6'}`}>
                      <h3
                        className={`${isFeatured ? 'text-3xl' : 'text-xl'} font-bold mb-4 group-hover:underline`}
                        style={{ color: '#303040' }}
                      >
                        {post.title}
                      </h3>
                      <p className={`${isFeatured ? 'text-lg' : 'text-base'} mb-4 leading-relaxed`} style={{ color: '#708080' }}>
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: '#70808020' }}>
                        <p className="text-sm" style={{ color: '#A060B0' }}>
                          {post.date} • {post.readTime}
                        </p>
                        <ArrowRight
                          className="h-5 w-5 group-hover:translate-x-1 transition-transform"
                          style={{ color: post.color }}
                        />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/blog">
              <Button
                variant="outline"
                size="lg"
                className="px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#606090', color: '#606090' }}
              >
                View All Articles
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section - AEO Optimized */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#303040' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-xl" style={{ color: '#708080' }}>
              Expert answers to common pharma and nutra export questions
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>
                What is pharmaceutical regulatory consultancy?
              </h3>
              <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                Pharmaceutical regulatory consultancy involves expert guidance on product registration, dossier preparation (ACTD/CTD/eCTD), compliance documentation, and regulatory approvals for pharma and nutraceutical exports. We help manufacturers navigate country-specific requirements for faster market entry.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>
                How long does pharma product registration take in Africa and Asia?
              </h3>
              <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                Registration timelines vary by country: Kenya (6-12 months), Tanzania (8-14 months), Cambodia (6-10 months), Nepal (8-12 months). Timeline depends on dossier completeness, regulatory complexity, and authority workload. Our structured approach reduces delays by 30-40%.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>
                What is regulatory gap analysis and why is it important?
              </h3>
              <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                Regulatory gap analysis identifies missing or incomplete documents in your dossier before submission. It checks COAs, stability data, GMP certificates, artwork, and API documentation against MoH requirements. This prevents rejections, queries, and costly resubmissions—essential for first-time exporters.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>
                Do you help SMEs with limited export experience?
              </h3>
              <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                Yes. Our services are designed specifically for small and medium pharmaceutical manufacturers entering Africa, Asia, GCC, and LATAM markets for the first time. We provide market selection guidance, documentation support, and step-by-step regulatory roadmaps tailored for SME budgets and timelines.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>
                Which countries do you support for pharma exports?
              </h3>
              <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                We support 30+ countries across four regions: <strong>Africa</strong> (Kenya, Nigeria, Tanzania, Uganda, Mauritius, Senegal, Togo, Benin, Mozambique, Ghana), <strong>Asia</strong> (Cambodia, Myanmar, Nepal, Sri Lanka, Philippines, Indonesia, Malaysia), <strong>GCC</strong> (UAE, Saudi Arabia, Qatar, Oman, Bahrain, Kuwait), and <strong>Latin America</strong> (Dominican Republic, Panama, Costa Rica, Uruguay, Paraguay, Chile).
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>
                What is included in your ACTD and CTD dossier preparation services?
              </h3>
              <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                Our dossier services include: complete Module 1-5 compilation, COA and stability data review, API/DMF documentation, GMP and CoPP verification, artwork and labeling compliance, technical summary writing, and deficiency handling. We ensure regulator-ready submissions aligned with ICH and country-specific guidelines.
              </p>
            </div>
          </div>

          {/* Schema Markup for FAQ */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is pharmaceutical regulatory consultancy?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Pharmaceutical regulatory consultancy involves expert guidance on product registration, dossier preparation (ACTD/CTD/eCTD), compliance documentation, and regulatory approvals for pharma and nutraceutical exports. We help manufacturers navigate country-specific requirements for faster market entry."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How long does pharma product registration take in Africa and Asia?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Registration timelines vary by country: Kenya (6-12 months), Tanzania (8-14 months), Cambodia (6-10 months), Nepal (8-12 months). Timeline depends on dossier completeness, regulatory complexity, and authority workload. Our structured approach reduces delays by 30-40%."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is regulatory gap analysis and why is it important?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Regulatory gap analysis identifies missing or incomplete documents in your dossier before submission. It checks COAs, stability data, GMP certificates, artwork, and API documentation against MoH requirements. This prevents rejections, queries, and costly resubmissions."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do you help SMEs with limited export experience?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. Our services are designed specifically for small and medium pharmaceutical manufacturers entering Africa, Asia, GCC, and LATAM markets. We provide market selection guidance, documentation support, and step-by-step regulatory roadmaps tailored for SME budgets."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Which countries do you support for pharma exports?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "We support 30+ countries across Africa (Kenya, Nigeria, Tanzania, Uganda, Mauritius, Senegal, Mozambique, Ghana), Asia (Cambodia, Myanmar, Nepal, Sri Lanka, Philippines), GCC (UAE, Saudi Arabia, Qatar), and Latin America (Dominican Republic, Panama, Costa Rica)."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is included in your ACTD and CTD dossier preparation services?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Our dossier services include complete Module 1-5 compilation, COA and stability data review, API/DMF documentation, GMP and CoPP verification, artwork and labeling compliance, technical summary writing, and deficiency handling."
                  }
                }
              ]
            })}
          </script>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div
            className="relative rounded-3xl overflow-hidden shadow-2xl"
            style={{ backgroundColor: '#303040' }}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 md:w-96 md:h-96 rounded-full opacity-10" style={{ backgroundColor: '#A060B0', transform: 'translate(30%, -30%)' }} />
            <div className="absolute bottom-0 left-0 w-48 h-48 md:w-64 md:h-64 rounded-full opacity-10" style={{ backgroundColor: '#606090', transform: 'translate(-30%, 30%)' }} />
            
            <div className="relative grid lg:grid-cols-2 gap-12 items-center p-12 md:p-16">
              <div className="text-left">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight">
                  Ready to Expand into Africa, Asia, GCC or LATAM?
                </h2>
                <p className="text-xl text-white/80 leading-relaxed">
                  Send us your product list and get a country-by-country regulatory roadmap.
                </p>
              </div>
              
              <div className="flex flex-col gap-4">
                <a 
                  href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
                  target="_blank" 
                  rel="noopener noreferrer external"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
                  }}
                >
                  <Button
                    size="lg"
                    className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 w-full"
                    style={{ backgroundColor: '#A060B0', color: 'white' }}
                  >
                    Book a Call
                    <Phone className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <Link to="/contact#send-us-your-details">
                  <Button
                    size="lg"
                    variant="outline"
                    className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 border-white text-white hover:bg-white/10 w-full"
                  >
                    Upload Product List
                    <Upload className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <div className="mt-4 flex items-center gap-6 justify-center text-white/60 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>Free Consultation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>Quick Response</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};